import MobileDrawer from '@c/drawers/MobileDrawer';
import CheckboxFilterSection from '@c/filters/components/CheckboxFilterSection';
import PriceRangeFilterSection from '@c/filters/components/PriceRangeFilterSection';
import RadioFilterSection from '@c/filters/components/RadioFilterSection';
import BaseModal from '@c/modals/BaseModal';
import DynamicSkeleton from '@c/skeletons/DynamicSkeleton';
import Button from '@ui/Button';
import { CONDITIONS } from '@util/createListingHelpers';
import type {
  PageSideFilters,
  TopFilters,
} from '@util/hooks/useProductsPage';
import { cn, isMobile } from '@util/index';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Condition, ProductSortOption } from 'models/product';
import type { SellerType } from 'models/shared';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import MobileLocationFilter, {
  type MobileLocationFilterHandle,
} from './MobileLocationFilter';
import { sortingOptions } from './VehicleForSaleControls';
import { SELLER_TYPE_OPTIONS } from './VehicleFilterSidebar';

const LocationFilterSection = dynamic(
  () => import('@c/filters/components/LocationFilterSection'),
  {
    ssr: false,
    loading: () => <DynamicSkeleton height={210} />,
  }
);

export type DrawerPage =
  | 'location'
  | 'sellerType'
  | 'condition'
  | 'manufacturer'
  | 'price'
  | 'sort';

const DRAWER_PAGE_TITLES: Record<DrawerPage, string> = {
  location: 'Location',
  sellerType: 'Seller type',
  condition: 'Condition',
  manufacturer: 'Manufacturer',
  price: 'Price',
  sort: 'Sort',
};

interface VehicleFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  sideFilters: PageSideFilters;
  onChange: (
    filtersOrUpdater:
      | PageSideFilters
      | ((prev: PageSideFilters) => PageSideFilters)
  ) => void;
  bikeMakes: string[];
  popularCities: string[];
  allCities: string[];
  topFilters: TopFilters;
  onTopFiltersChange: (filters: TopFilters) => void;
  /** When set, the mobile drawer opens directly on the given page instead of
   *  the filter menu. Reset to null after dismiss. */
  initialPage?: DrawerPage | null;
}

// Mobile filter drawer (menu + per-filter sub-pages) plus the desktop
// `BaseModal` fallback. Owns its own active-page state.
const VehicleFilterDrawer = ({
  open,
  onClose,
  sideFilters,
  onChange,
  bikeMakes,
  popularCities,
  allCities,
  topFilters,
  onTopFiltersChange,
  initialPage = null,
}: VehicleFilterDrawerProps) => {
  const [activeDrawerPage, setActiveDrawerPage] = useState<DrawerPage | null>(
    initialPage
  );
  const [isMobileState, setIsMobileState] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const locationFilterRef = useRef<MobileLocationFilterHandle>(null);
  const applyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobileState(isMobile());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync activeDrawerPage with initialPage whenever the drawer is (re)opened.
  useEffect(() => {
    if (open) setActiveDrawerPage(initialPage);
  }, [open, initialPage]);

  // Clean up pending timeout on unmount.
  useEffect(() => {
    return () => {
      if (applyTimeoutRef.current) clearTimeout(applyTimeoutRef.current);
    };
  }, []);

  const dismiss = () => {
    onClose();
    setActiveDrawerPage(null);
  };

  const sellerTypeState = sideFilters.sellerType ?? [];
  const conditionState = sideFilters.bikeConditions ?? [];
  const makesState = sideFilters.bikeMakes ?? [];

  // Shared filter sections used by the desktop modal fallback.
  const sharedFilters = (
    <>
      <CheckboxFilterSection
        title="Seller type"
        filterOptions={SELLER_TYPE_OPTIONS.map((opt) => ({
          key: opt.key,
          label: opt.label,
          id: opt.key,
        }))}
        onChange={(filters) => {
          onChange({ ...sideFilters, sellerType: filters as SellerType[] });
        }}
        filterState={sellerTypeState}
      />
      <RadioFilterSection
        title="Condition"
        titleClassName="text-[1.8rem]"
        filterOptions={CONDITIONS.map((c) => ({
          key: c.replaceAll(' ', '-'),
          label: c,
        }))}
        onChange={(filters) => {
          onChange({
            ...sideFilters,
            bikeConditions: filters as Condition[],
          });
        }}
        initialFilters={conditionState as string[]}
      />
      <CheckboxFilterSection
        title="Manufacturer"
        filterOptions={bikeMakes.map((make) => ({
          key: make,
          label: make,
          id: make,
        }))}
        onChange={(filters) => {
          onChange({ ...sideFilters, bikeMakes: filters });
        }}
        filterState={makesState}
      />
      <PriceRangeFilterSection
        title="Price"
        titleClassName="text-[1.8rem]"
        onChange={(min, max) => {
          onChange({ ...sideFilters, minPrice: min, maxPrice: max });
        }}
        min={sideFilters.minPrice}
        max={sideFilters.maxPrice}
      />
    </>
  );

  if (!isMobileState) {
    return (
      <BaseModal isOpen={open} dismiss={dismiss} hideCloseIcon>
        <div className="overflow-y-scroll p-8">
          <LocationFilterSection
            radius={sideFilters.radius ?? 25}
            setRadius={(radius) => {
              onChange((prev) => ({ ...prev, radius }));
            }}
            location={sideFilters.location}
            popularCities={popularCities}
            allCities={allCities}
            setLocation={(location) => {
              onChange((prev) => ({ ...prev, location }));
            }}
          />
          {sharedFilters}
          <div className="p-4">
            <Button
              width="fluid"
              type="secondary"
              onClick={onClose}
              text="Apply"
            />
          </div>
        </div>
      </BaseModal>
    );
  }

  return (
    <MobileDrawer
      show={open}
      dismiss={dismiss}
      title={
        activeDrawerPage ? DRAWER_PAGE_TITLES[activeDrawerPage] : 'Filters'
      }
      headerExtraButton={
        activeDrawerPage ? (
          <button
            type="button"
            onClick={() => setActiveDrawerPage(null)}
            className="flex items-center gap-2 text-[2.1rem] font-medium text-brand-secondary"
          >
            <ChevronLeftIcon className="h-10 w-10 text-brand-secondary" />
            Back
          </button>
        ) : null
      }
    >
      <div className="p-8 pb-[12rem]">
        {!activeDrawerPage && (
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setActiveDrawerPage('location')}
              className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
            >
              <span className="text-[2.1rem] font-semibold text-black">
                Location
              </span>
              <div className="flex items-center gap-2">
                {sideFilters.location && (
                  <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                    {sideFilters.location.address}
                  </span>
                )}
                <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveDrawerPage('sellerType')}
              className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
            >
              <span className="text-[2.1rem] font-semibold text-black">
                Seller type
              </span>
              <div className="flex items-center gap-2">
                {!!sellerTypeState.length && (
                  <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                    {sellerTypeState
                      .map(
                        (s) =>
                          SELLER_TYPE_OPTIONS.find((opt) => opt.key === s)
                            ?.label ?? s
                      )
                      .join(', ')}
                  </span>
                )}
                <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveDrawerPage('condition')}
              className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
            >
              <span className="text-[2.1rem] font-semibold text-black">
                Condition
              </span>
              <div className="flex items-center gap-2">
                {!!conditionState.length && (
                  <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                    {conditionState.join(', ')}
                  </span>
                )}
                <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveDrawerPage('manufacturer')}
              className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
            >
              <span className="text-[2.1rem] font-semibold text-black">
                Manufacturer
              </span>
              <div className="flex items-center gap-2">
                {!!makesState.length && (
                  <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                    {makesState.join(', ')}
                  </span>
                )}
                <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveDrawerPage('price')}
              className="flex w-full items-center justify-between border-b border-zinc-300 px-2 py-8"
            >
              <span className="text-[2.1rem] font-semibold text-black">
                Price
              </span>
              <div className="flex items-center gap-2">
                {(sideFilters.minPrice || sideFilters.maxPrice) && (
                  <span className="w-fit max-w-[20rem] truncate text-[1.6rem] font-medium text-zinc-400">
                    {sideFilters.minPrice && sideFilters.maxPrice
                      ? `$${sideFilters.minPrice} - $${sideFilters.maxPrice}`
                      : sideFilters.minPrice
                      ? `Over $${sideFilters.minPrice}`
                      : `Up to $${sideFilters.maxPrice}`}
                  </span>
                )}
                <ChevronRightIcon className="h-12 w-12 text-zinc-400" />
              </div>
            </button>
          </div>
        )}

        {activeDrawerPage === 'location' && (
          <MobileLocationFilter
            ref={locationFilterRef}
            radius={sideFilters.radius ?? 25}
            setRadius={(radius) => {
              onChange((prev) => ({ ...prev, radius }));
            }}
            location={sideFilters.location}
            popularCities={popularCities}
            setLocation={(location) => {
              onChange((prev) => ({ ...prev, location }));
            }}
          />
        )}

        {activeDrawerPage === 'sellerType' && (
          <div className="flex flex-col space-y-4">
            {SELLER_TYPE_OPTIONS.map((opt) => {
              const isSelected = sellerTypeState.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    const newSellerTypes = isSelected
                      ? sellerTypeState.filter((s) => s !== opt.key)
                      : [...sellerTypeState, opt.key];
                    onChange({
                      ...sideFilters,
                      sellerType: newSellerTypes,
                    });
                  }}
                  className={cn(
                    'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                    isSelected && 'bg-zinc-100'
                  )}
                >
                  <div className="flex items-center gap-2">{opt.label}</div>
                  {isSelected && (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {activeDrawerPage === 'manufacturer' && (
          <div className="flex flex-col space-y-4">
            {bikeMakes.map((make) => {
              const isSelected = makesState.includes(make);
              return (
                <button
                  key={make}
                  type="button"
                  onClick={() => {
                    const newMakes = isSelected
                      ? makesState.filter((m) => m !== make)
                      : [...makesState, make];
                    onChange({ ...sideFilters, bikeMakes: newMakes });
                  }}
                  className={cn(
                    'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                    isSelected && 'bg-zinc-100'
                  )}
                >
                  <div className="flex items-center gap-2">{make}</div>
                  {isSelected && (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {activeDrawerPage === 'condition' && (
          <div className="flex flex-col space-y-4">
            {CONDITIONS.map((c) => {
              const key = c.replaceAll(' ', '-');
              const isSelected = conditionState.includes(key as Condition);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    const newConditions = isSelected
                      ? []
                      : ([key] as Condition[]);
                    onChange({
                      ...sideFilters,
                      bikeConditions: newConditions,
                    });
                  }}
                  className={cn(
                    'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                    isSelected && 'bg-zinc-100'
                  )}
                >
                  <div className="flex items-center gap-2">{c}</div>
                  {isSelected && (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {activeDrawerPage === 'sort' && (
          <div className="flex flex-col space-y-4">
            {sortingOptions.map((opt) => {
              const isSelected = topFilters.sort === opt.value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onTopFiltersChange({
                      ...topFilters,
                      sort: opt.value as ProductSortOption,
                    });
                  }}
                  className={cn(
                    'flex w-full justify-between px-4 py-6 text-left text-[2rem] font-medium sm:py-2 sm:text-[1.5rem]',
                    isSelected && 'bg-zinc-100'
                  )}
                >
                  <div className="flex items-center gap-2">{opt.label}</div>
                  {isSelected && (
                    <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-brand-secondary p-2">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {activeDrawerPage === 'price' && (
          <div className="flex flex-col space-y-16 pb-8">
            <div className="flex w-full items-center justify-between gap-12">
              <div className="flex w-full items-center justify-between rounded-2xl bg-[#F1F1F1] p-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={sideFilters.minPrice || ''}
                  onChange={(e) => {
                    onChange({
                      ...sideFilters,
                      minPrice: e.target.value ? Number(e.target.value) : 0,
                    });
                  }}
                  className="w-full border-none bg-transparent p-4 text-[1.6rem] outline-none"
                />
                <span className="pr-4 text-[1.6rem] font-semibold text-zinc-700">
                  USD
                </span>
              </div>
              <span className="text-[1.8rem] font-medium text-zinc-700">
                to
              </span>
              <div className="flex w-full items-center justify-between rounded-2xl bg-[#F1F1F1] p-2">
                <input
                  type="number"
                  placeholder="Max"
                  value={sideFilters.maxPrice || ''}
                  onChange={(e) => {
                    onChange({
                      ...sideFilters,
                      maxPrice: e.target.value ? Number(e.target.value) : 0,
                    });
                  }}
                  className="w-full border-none bg-transparent p-4 text-[1.6rem] outline-none"
                />
                <span className="pr-4 text-[1.6rem] font-semibold text-zinc-700">
                  USD
                </span>
              </div>
            </div>
            {(!!sideFilters.minPrice || !!sideFilters.maxPrice) && (
              <div className="flex w-full justify-center">
                <button
                  type="button"
                  className="text-[1.8rem] font-semibold text-brand-secondary"
                  onClick={() => {
                    onChange({ ...sideFilters, minPrice: 0, maxPrice: 0 });
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="absolute bottom-0 w-full border-t border-zinc-200 bg-white p-6">
        <Button
          width="fluid"
          type="secondary"
          className="h-[5.5rem] rounded-[2rem] text-[2.1rem] font-semibold text-white"
          onClick={() => {
            if (activeDrawerPage === 'location') {
              locationFilterRef.current?.apply();
              setIsApplying(true);
              applyTimeoutRef.current = setTimeout(() => {
                setActiveDrawerPage(null);
                setIsApplying(false);
              }, 800);
            } else {
              setIsApplying(true);
              applyTimeoutRef.current = setTimeout(() => {
                onClose();
                setIsApplying(false);
              }, 800);
            }
          }}
          text={activeDrawerPage === 'location' ? 'Apply' : 'Show Results'}
          loading={isApplying}
          disabled={isApplying}
        />
      </div>
    </MobileDrawer>
  );
};

export default VehicleFilterDrawer;
